import { APPS } from "./apps";

// Natural-language aliases → canonical app id.
// Used by both the "open <app>" regex path AND the single-keyword/short-phrase path
// so phrases like "open emails", "show my schedule" all resolve correctly.
const APP_ALIASES = {
  // Mail
  "mail": "mail", "mails": "mail", "email": "mail", "emails": "mail",
  "inbox": "mail", "hexa mail": "mail", "gmail": "mail",
  // Calendar
  "calendar": "calendar", "schedule": "calendar", "my schedule": "calendar",
  "today's schedule": "calendar", "todays schedule": "calendar",
  "events": "calendar", "calender": "calendar",
  // Chat / Assistant
  "chat": "chat", "hexa chat": "chat", "assistant": "chat", "ai": "chat", "hexa": "chat",
  // Team Chat / DMs
  "team chat": "teamchat", "spaces": "teamchat", "dm": "teamchat", "dms": "teamchat",
  "messages": "teamchat", "messenger": "teamchat",
  // Meetings
  "meeting": "meetings", "meetings": "meetings", "video call": "meetings",
  "call": "meetings", "videocall": "meetings", "conference": "meetings",
  // Security
  "security": "security", "antivirus": "security", "shield": "security",
  "hexa shield": "security",
  // Files
  "files": "files", "file manager": "files", "documents": "files",
  // Terminal
  "terminal": "terminal", "console": "terminal", "shell": "terminal",
  // Calculator
  "calculator": "calculator", "calc": "calculator", "calculate": "calculator",
  // Tasks
  "tasks": "tasks", "task": "tasks", "todo": "tasks", "to do": "tasks", "to-do": "tasks",
  "my tasks": "tasks",
  // Browser
  "browser": "browser", "web": "browser", "internet": "browser",
  // Image
  "image studio": "imagegen", "image": "imagegen", "images": "imagegen",
  "generate image": "imagegen", "imagegen": "imagegen",
  // Notes
  "note": "notes", "notes": "notes", "my notes": "notes",
  // Screen share / record
  "screen recorder": "recorder", "recorder": "recorder", "screen record": "recorder",
  "screen share": "recorder", "share screen": "recorder", "share my screen": "recorder",
  // Shortcuts
  "shortcuts": "shortcuts", "keyboard": "shortcuts", "keyboard shortcuts": "shortcuts",
  // Docs
  "word": "word", "document": "word", "doc": "word",
  "excel": "excel", "spreadsheet": "excel", "sheet": "excel",
  "slides": "ppt", "presentation": "ppt", "powerpoint": "ppt", "ppt": "ppt",
  "pdf": "pdf",
  "audio": "audio", "music": "audio",
  "video": "video",
  // Workforce
  "attendance": "attendance", "time clock": "attendance", "clock in": "attendance",
  "clock out": "attendance", "my team": "attendance", "team attendance": "attendance",
  // Dashboard / Wingman
  "dashboard": "dashboard", "home": "dashboard", "overview": "dashboard",
  "wingman": "wingman", "agent": "wingman", "ai agent": "wingman", "workflow": "wingman",
  // Approvals
  "approvals": "approvals", "approve": "approvals", "drafts": "approvals", "ai drafts": "approvals",
  // Social
  "social": "social", "social media": "social", "post": "social", "posts": "social",
};

function resolveAppFromPhrase(name) {
  const n = (name || "").toLowerCase().trim().replace(/\sapp$/, "");
  if (!n) return null;
  if (APP_ALIASES[n]) return APP_ALIASES[n];
  // Try matching app titles + ids (loose contains)
  const app = APPS.find(a =>
    a.title.toLowerCase().includes(n) ||
    a.id === n ||
    n.includes(a.id)
  );
  if (app) return app.id;
  // Last resort: try each alias as a substring
  for (const [alias, id] of Object.entries(APP_ALIASES)) {
    if (n.includes(alias)) return id;
  }
  return null;
}

// Tries to match natural-language voice command → app action.
// Returns { action, appId } or null. Action handlers run in TopBar / Dashboard.
export function parseVoiceCommand(text) {
  const t = (text || "").toLowerCase().trim().replace(/[.?!]+$/, "");
  if (!t) return null;

  // "open / launch / start / show / go to <X>"
  const openMatch = t.match(/^(open|launch|start|show|go to|switch to|take me to|bring up)\s+(.+)$/);
  if (openMatch) {
    const appId = resolveAppFromPhrase(openMatch[2]);
    if (appId) return { action: "open", appId };
  }

  // "what's my schedule today" / "what is my schedule" / "today's schedule"
  if (/(my\s+)?(schedule|agenda|calendar|events?)\b.*(today|this week)?/.test(t)
      || /what.?s.*(schedule|agenda|calendar)/.test(t)
      || /show.*(schedule|agenda|events?)/.test(t)) {
    return { action: "open", appId: "calendar" };
  }

  // "check my emails" / "any new emails" / "read my inbox"
  if (/(check|read|see|any new)\s+(my\s+)?(mail|email|emails|inbox)/.test(t)) {
    return { action: "open", appId: "mail" };
  }

  // "new <thing>"
  if (/^(new|start)\s+(chat|conversation)$/.test(t)) return { action: "open", appId: "chat" };
  if (/^(new|compose|write|send)\s+(mail|email|message)$/.test(t)) return { action: "open", appId: "mail" };
  if (/^(new|schedule|create|start)\s+(meeting|call|videocall|video call|conference)$/.test(t)) return { action: "open", appId: "meetings" };
  if (/^(new|create|add)\s+(note|notes)$/.test(t)) return { action: "open", appId: "notes" };
  if (/^(new|add|create)\s+task$/.test(t)) return { action: "open", appId: "tasks" };
  if (/^(new|create|schedule)\s+event$/.test(t)) return { action: "open", appId: "calendar" };
  if (/^(share|start)\s+(my\s+)?screen$/.test(t)) return { action: "open", appId: "recorder" };

  // Plain keyword (e.g., user just said "email")
  const direct = resolveAppFromPhrase(t);
  if (direct) return { action: "open", appId: direct };

  // commands
  if (/^(scan|run scan|security scan)$/.test(t)) return { action: "scan", appId: "security" };
  if (/^(what time|time)/.test(t)) return { action: "time" };
  if (/^(what day|today.?s date|date)$/.test(t)) return { action: "date" };
  if (/^sign out$/.test(t) || /^log ?out$/.test(t)) return { action: "logout" };
  if (/^(stop|cancel|quiet|shut up|silence)$/.test(t)) return { action: "stop" };
  return null;
}

export function speak(text) {
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.02; u.pitch = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {}
}

/** Hard-cancel any in-progress TTS and clear the queue. */
export function stopSpeaking() {
  try {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  } catch {}
}


/* ============================================================================
   Microphone diagnostics

   The Web Speech API reports "muted / wrong input device" and "you didn't speak
   in time" as the same `no-speech` error, so users get one vague message for two
   unrelated problems and no way to tell which they have. probeMic() samples the
   input for ~1s first so the two can be told apart.

   Deliberately conservative: only a perfectly flat signal (peak === 0) counts as
   a dead stream, because a quiet room plus Chrome's noise suppression can read
   very low. Callers must treat a bad verdict as advisory and still attempt
   recognition — a misread must never be the reason voice refuses to run.
   ========================================================================== */
export async function probeMic() {
  // DISABLED. Acquiring and releasing the microphone here, immediately before
  // SpeechRecognition starts, can leave recognition unable to open the device
  // — voice went from working to silent. Returning "skip" keeps every caller
  // intact (better error wording, retry-once) without ever taking the mic.
  return { verdict: "skip" };
  // eslint-disable-next-line no-unreachable
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!navigator.mediaDevices?.getUserMedia || !AC) return { verdict: "skip" };

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    const n = e?.name || "";
    if (n === "NotAllowedError" || n === "SecurityError") return { verdict: "blocked" };
    if (n === "NotFoundError" || n === "DevicesNotFoundError") return { verdict: "no-device" };
    return { verdict: "skip" };
  }

  try {
    const ctx = new AC();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    let peak = 0;
    const until = Date.now() + 1000;
    while (Date.now() < until) {
      analyser.getByteTimeDomainData(buf);
      for (let i = 0; i < buf.length; i++) {
        const dev = Math.abs(buf[i] - 128);
        if (dev > peak) peak = dev;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 50));
    }
    try { await ctx.close(); } catch (_) { /* noop */ }
    return { verdict: peak === 0 ? "silent" : "ok", peak };
  } catch (_) {
    return { verdict: "skip" };
  } finally {
    try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
  }
}

/** Human-readable text for a SpeechRecognition error code. */
export function voiceErrorText(code, { deadStream = false } = {}) {
  if (code === "no-speech") {
    return deadStream
      ? "Your microphone isn't sending any sound. In Chrome, click the padlock in the address bar and check which microphone this site is using, and that it isn't muted."
      : "Didn't catch anything — start speaking as soon as the mic turns blue, it only listens for a few seconds.";
  }
  const map = {
    "not-allowed": "Microphone blocked — allow mic access for this site, then try again.",
    "service-not-allowed": "Microphone blocked — allow mic access for this site, then try again.",
    "audio-capture": "No microphone detected on this device.",
    "network": "Network error reaching the speech service.",
    "aborted": "Voice cancelled.",
    "language-not-supported": "Language not supported.",
  };
  return map[code] || `Voice error: ${code}`;
}

/** Text for a probeMic verdict that should stop us before recognition starts. */
export function micBlockerText(verdict) {
  if (verdict === "blocked") return "Microphone blocked — allow mic access for this site, then try again.";
  if (verdict === "no-device") return "No microphone found on this device.";
  return null;
}
